/**
 * Injectable schema registry.
 *
 * `getSchema(id)` resolves a schema id to its row. The lookup is host-supplied —
 * a consumer wires one with `registerSchemas`; until then it reads `null`. Pure:
 * no client or store framework, so the field and collection layers can depend on
 * it freely.
 */

import type { SavedFilter } from "../fields/filter.js"
import type { FieldTypeName } from "../fields/types.js"

export interface SchemaField {
  key: string
  type: FieldTypeName
  label?: string
  /** Record-type scoping for link/record/type fields (e.g. "organization"). */
  recordType?: string | null
  /** The wire format of a reverse-link filter field (`reverse_links`). */
  subtype?: string | null
  isArray?: boolean
  values?: unknown[]
  description?: string | null
  /** Hidden from the default views; still a declared field. */
  isHidden?: boolean
  /** A `lookup` field's path: `<link field>.<field on the linked record>`. */
  path?: string
  /** A time field's tracked key: core logs a span each time that value changes
   *  (see services/spans.ts). `listing` tracks list membership. */
  tracks?: string
  /** A link field's filter on the records it can link to, run on its
   *  `recordType`: the picker offers only matches, and a write adding any
   *  other target is refused. Links already outside it stay. */
  filter?: SavedFilter
}

/** JSONB blob stored on `records.layout` / `schemas.layout`. */
export interface SchemaLayout {
  version?: number
  pages?: unknown[]
}

/** Common-subset shape for anything that only reads schema properties. `Schema`
 *  and looser type-store shapes both satisfy it. */
export interface SchemaLike {
  id?: number
  name?: string
  record_type?: string | null
  fields?: SchemaField[]
  description?: string | null
  layout?: SchemaLayout | null
  icon?: string | null
  color?: string | null
}

export interface Schema extends SchemaLike {
  id: number
  name: string
  record_type: string | null
}

let resolver: (id: number) => Schema | null = () => null

/** Wire the host's schema lookup. */
export function registerSchemas(fn: (id: number) => Schema | null): void {
  resolver = fn
}

export const getSchema = (id: number): Schema | null => resolver(id)
